import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Settings, Activity, Brain, ChevronRight, X } from 'lucide-react';
import { JournalEntry } from '../components/journal/JournalEntry';
import { BehaviorManager } from '../components/journal/BehaviorManager';
import { JournalImpacts } from '../components/journal/JournalImpacts';
import { JournalMetrics } from '../components/journal/JournalMetrics';
import { JournalExitDialog } from '../components/journal/JournalExitDialog';
import { useNavigate } from 'react-router-dom';
import { HabitTracker } from '../components/habits/HabitTracker';
import { HabitAnalytics } from '../components/habits/HabitAnalytics';

const tabs = [
  {
    id: 'daily',
    label: 'Log Entry',
    icon: <BookOpen className="w-4 h-4" />,
    description: 'Record your daily behaviors and notes'
  },
  {
    id: 'behaviors',
    label: 'Select Behaviors',
    icon: <Settings className="w-4 h-4" />,
    description: 'Customize tracked behaviors'
  },
  {
    id: 'impacts',
    label: 'Analysis',
    icon: <Activity className="w-4 h-4" />,
    description: 'View behavior impact analysis'
  }
];

export function JournalPage() {
  const [activeTab, setActiveTab] = useState('daily');
  const [showExitDialog, setShowExitDialog] = useState(false);
  const navigate = useNavigate();

  const handleExit = () => {
    if (activeTab === 'daily') {
      setShowExitDialog(true);
    } else {
      navigate('/home');
    }
  };

  const handleSave = async () => {
    // Handle any save operations
    navigate('/home');
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'daily':
        return (
          <div className="space-y-6">
            <JournalMetrics />
            <HabitTracker onSave={handleSave} />
          </div>
        );
      case 'behaviors':
        return <BehaviorManager />;
      case 'impacts':
        return (
          <div className="space-y-6">
            <JournalImpacts />
            <HabitAnalytics />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Metaverse-inspired background with soft gradients */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-purple-500/5 to-background pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_var(--tw-gradient-stops))] from-blue-500/5 via-cyan-500/5 to-transparent" />
        <div className="absolute inset-0 bg-[conic-gradient(from_90deg_at_80%_50%,_var(--tw-gradient-stops))] from-primary/5 via-purple-500/5 to-transparent opacity-30" />
        <div className="absolute inset-0 backdrop-blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 py-6 pt-20 pb-24 relative">
        <div className="space-y-6">
          {/* Header with Scientific Look */}
          <div className="theme-card p-4 bg-background/50 backdrop-blur-xl border-primary/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Brain className="w-6 h-6 text-primary" />
                  <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Journal Analysis</h1>
                  <p className="text-sm text-muted-foreground">Track, analyze, optimize</p>
                </div>
              </div>
              <button
                onClick={handleExit}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="grid grid-cols-3 gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`p-4 rounded-lg transition-all relative overflow-hidden backdrop-blur-sm ${
                  activeTab === tab.id 
                    ? 'bg-primary/10 border border-primary/20'
                    : 'bg-muted/30 hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    {tab.icon}
                    {activeTab === tab.id && (
                      <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                    )}
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-sm">{tab.label}</div>
                    <div className="text-xs text-muted-foreground">{tab.description}</div>
                  </div>
                  <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${
                    activeTab === tab.id ? 'rotate-90' : ''
                  }`} />
                </div>
              </button>
            ))}
          </div>

          {/* Content Area */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="relative min-h-[60vh] backdrop-blur-sm"
          >
            {renderContent()}
          </motion.div>
        </div>
      </div>

      {/* Exit Dialog */}
      {showExitDialog && (
        <JournalExitDialog
          onClose={() => setShowExitDialog(false)}
          onContinue={() => setShowExitDialog(false)}
          onDismiss={() => navigate('/home')}
        />
      )}
    </>
  );
}